import baseAos from 'aos';
import 'aos/dist/aos.css';
import { isMobile } from 'detect-mobile-device';

/**
 * AOS 动画工具
 *
 * @example
 * // 初始化（瀑布流页面）
 * import aos from 'utils/aos';
 * aos.init(); // 或 aos.init(aos.MODE.WATERFALL);
 *
 * // 初始化（整屏切换页面）
 * aos.init(aos.MODE.FULLSCREEN);
 *
 * // 在 DOM 节点中使用动画
 * <div className="rule-group-2" {...aos.FADE_UP()} >
 *
 */

const $aos = baseAos;

/**
 * AOS 初始化模式常量
 * @type {{WATERFALL: string, FULLSCREEN: string}}
 */
const MODE = {
  /** 瀑布流模式 - 适合普通滚动页面 */
  WATERFALL: 'waterfall',
  /** 整屏切换模式 - 适合全屏滚动页面（如 pageSwiper） */
  FULLSCREEN: 'fullscreen'
};

/**
 * 获取 offset 值
 * @param {string} mode - 页面类型: MODE.WATERFALL | MODE.FULLSCREEN
 * @returns {number}
 */
const getOffset = mode => {
  // 移动端统一使用 -9999，立即显示所有元素
  if (isMobile()) {
    return -9999;
  }

  // 根据模式返回不同的默认 offset
  if (mode === MODE.FULLSCREEN) {
    // 整屏切换模式：使用视口高度的 1.5 倍
    // 确保整屏页面底部元素也能被触发
    return window.innerHeight * 1.5;
  }

  // 瀑布流模式：使用固定值 120
  return 120;
};

/**
 * 初始化 aos
 * @param {string} mode - 页面类型: aos.MODE.WATERFALL | aos.MODE.FULLSCREEN，默认 WATERFALL
 */
const init = (mode = MODE.WATERFALL) => {
  const validModes = [MODE.WATERFALL, MODE.FULLSCREEN];
  if (!validModes.includes(mode)) {
    console.warn(`[AOS] Invalid mode "${mode}", fallback to "waterfall"`);
    mode = MODE.WATERFALL;
  }

  $aos.init({
    disable: false,
    debounceDelay: 300,
    offset: getOffset(mode)
  });
};

/**
 * 再次初始化（向后兼容，默认使用瀑布流模式）
 */
const reInit = () => {
  init(MODE.WATERFALL);
};

const refreshHard = () => {
  $aos.refreshHard();
};

// 声明缓存变量
const caches = {};

/**
 * 创建Aos对象, 进行同类aos缓存
 * @param data
 * @returns {*}
 */
const createAosObject = data => {
  if (!caches[data]) {
    // noinspection JSUnresolvedFunction
    caches[data] = Object.freeze({ 'data-aos': data });
  }
  return caches[data];
};

// aos 官网提供滚动样式库
const animateObj = {
  FADE_UP: 'fade-up',
  FADE_DOWN: 'fade-down',
  FADE_LEFT: 'fade-left',
  FADE_RIGHT: 'fade-right',
  FADE_UP_LEFT: 'fade-up-left',
  FADE_UP_RIGHT: 'fade-up-right',
  FADE_DOWN_LEFT: 'fade-down-left',
  FADE_DOWN_RIGHT: 'fade-down-right',
  ZOOM_IN: 'zoom-in',
  ZOOM_IN_UP: 'zoom-in-up',
  ZOOM_IN_DOWN: 'zoom-in-down',
  ZOOM_IN_LEFT: 'zoom-in-left',
  ZOOM_IN_RIGHT: 'zoom-in-right',
  ZOOM_OUT: 'zoom-out',
  ZOOM_OUT_UP: 'zoom-out-up',
  ZOOM_OUT_DOWN: 'zoom-out-down',
  ZOOM_OUT_LEFT: 'zoom-out-left',
  ZOOM_OUT_RIGHT: 'zoom-out-right',
  FLIP_UP: 'flip-up',
  FLIP_DOWN: 'flip-down',
  FLIP_LEFT: 'flip-left',
  FLIP_RIGHT: 'flip-right'
};

/**
 * 输出的aos动画类型
 * @type {{MODE: {WATERFALL: string, FULLSCREEN: string}, init: (function(string): void), reInit: (function(): void), refreshHard: (function(): void), ZOOM_IN_LEFT: (function(): *), FADE_UP: (function(): *), ZOOM_IN_DOWN: (function(): *), ZOOM_IN_RIGHT: (function(): *), FLIP_DOWN: (function(): *), FLIP_LEFT: (function(): *), FLIP_UP: (function(): *), ZOOM_OUT_UP: (function(): *), FADE_DOWN: (function(): *), ZOOM_OUT_RIGHT: (function(): *), ZOOM_IN: (function(): *), ZOOM_OUT: (function(): *), FADE_RIGHT: (function(): *), ZOOM_OUT_DOWN: (function(): *), FADE_LEFT: (function(): *), ZOOM_OUT_LEFT: (function(): *), FLIP_RIGHT: (function(): *), ZOOM_IN_UP: (function(): *)}}
 */
const aos = {
  MODE,
  init: mode => init(mode),
  reInit: () => reInit(),
  refreshHard: () => refreshHard(),
  ZOOM_IN: () => createAosObject(animateObj.ZOOM_IN),
  ZOOM_IN_UP: () => createAosObject(animateObj.ZOOM_IN_UP),
  ZOOM_IN_DOWN: () => createAosObject(animateObj.ZOOM_IN_DOWN),
  ZOOM_IN_LEFT: () => createAosObject(animateObj.ZOOM_IN_LEFT),
  ZOOM_IN_RIGHT: () => createAosObject(animateObj.ZOOM_IN_RIGHT),
  ZOOM_OUT: () => createAosObject(animateObj.ZOOM_OUT),
  ZOOM_OUT_UP: () => createAosObject(animateObj.ZOOM_OUT_UP),
  ZOOM_OUT_DOWN: () => createAosObject(animateObj.ZOOM_OUT_DOWN),
  ZOOM_OUT_LEFT: () => createAosObject(animateObj.ZOOM_OUT_LEFT),
  ZOOM_OUT_RIGHT: () => createAosObject(animateObj.ZOOM_OUT_RIGHT),
  FADE_UP: () => createAosObject(animateObj.FADE_UP),
  FADE_DOWN: () => createAosObject(animateObj.FADE_DOWN),
  FADE_LEFT: () => createAosObject(animateObj.FADE_LEFT),
  FADE_RIGHT: () => createAosObject(animateObj.FADE_RIGHT),
  FLIP_UP: () => createAosObject(animateObj.FADE_UP),
  FLIP_DOWN: () => createAosObject(animateObj.FADE_UP),
  FLIP_LEFT: () => createAosObject(animateObj.FADE_UP),
  FLIP_RIGHT: () => createAosObject(animateObj.FADE_UP)
};

export default aos;
